import React, { useMemo } from 'react';
import { useState, useEffect, useRef } from 'react';
import { Document } from "shared/models/site/document"
import { addDocument, editDocument, getDocument, rescindDocument as recindDocument } from "shared/services/railDocumentsApi";
import { Modal, ModalHeader } from 'reactstrap';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { AddDocumentH2, EditDocumentH2, RescindDocumentH2, ModalClose } from "components/menu/Labels";
import InfoModal, { ExitCancelModel } from "components/ui/InfoModal"
import './DocumentAdmin.css'
import { documentMapper, toISOLocalDate } from "shared/models/mapping/mapping";
import { documentAddEditState } from './AddEditDocumentState'
import { SecondPage } from './AddEditDocumentSecondPage';
import { FirstPage } from './AddEditDocumentFirstPage';
import { getUserExistingRulebookMode } from '../../../../shared/store/applicationState';

export enum PageView {
    AddNew = 0,
    Edit = 1,
    NewVersion = 2,
    Rescind = 3
}

export const ModalSubHeader = (props: { children?: React.ReactNode }) => {
    return (
        <div className="modal-sub-header mb-3"><h3 className="modal-sub-header-bg">{props.children}</h3></div>
    );
}

type IDocumentFormProps = {
    isOpen: boolean,
    onUpdate: () => void,
    onClose?: () => void,
    documentId?: number,
    viewType: PageView,
    setOpen?: (b: boolean) => void
}

type IAddNewFormProps = {
    isOpen: boolean,
    onUpdate: () => void,
    onClose?: () => void,
}

type IEditFormProps = IAddNewFormProps & {
    documentId: number
};

const DocumentForm = (props: IDocumentFormProps) => {
    const [view, setView] = useState(props.viewType);
    const documentId = useRef(-1);

    var successText: string = 'Document information has been updated';
    var errorText: string = 'An error occured performing the requested action';
    var confirmCloseText: string = 'Are you sure you want to exit?\nUnsaved data will be lost.';

    const closeModal = () => {
        if (view === PageView.NewVersion) setView(PageView.Edit);
        documentId.current = -1;
        documentAddEditState.reset();
        if (props.onClose) { props.onClose(); }
    };

    useEffect(() => {
        const mount = async () => {
            if (props.documentId) {
                const currentMode = getUserExistingRulebookMode();
                const doc = documentMapper.mapFromRailDocument(await getDocument(props.documentId, currentMode!));

                documentAddEditState.setState(x => {
                    x.document = doc;
                    // Move to a configuration function if more defaults required for different views
                    x.document.requiresAcknowledgementAndNotify = props.viewType === PageView.Rescind ? true : x.document.requiresAcknowledgementAndNotify;
                    x.originalDocument = doc
                });
            } else {
                documentAddEditState.reset();
            }
        };
        mount();

        return () => {
        };
    },
        [props.documentId, props.viewType]);

    const handleErrors = (e: any): string | undefined => {
        if (e.title) {
            var errorText = e.title;

            if (Array.isArray(e.errors.ValidationErrors)) {
                e.errors.ValidationErrors.map((x: string) => {
                    errorText += `\n${x}`;
                });
            }
            return errorText;
        }
    }

    const AddNewForm = () => {
        const [page, setPage] = useState(1);
        const [isError, setIsError] = useState(false);
        const [isComplete, setIsComplete] = useState(false);
        const [isConfirmClose, setIsConfirmClose] = useState(false);

        const addNewUpload = async (document: Document) => {
            const newDoc = documentMapper.mapToNewDocument(document);
            await addDocument(newDoc)
                .then(async (response) => {
                    if (response.ok) {
                        props.onUpdate();
                        setIsComplete(true);
                    }
                    else {
                        throw (await response.json());
                    }
                })
                .catch((e) => {
                    errorText = handleErrors(e) || errorText;
                    setIsError(true);
                });
        }

        const closeModalPrompt = () => {
            setIsConfirmClose(true);
        }

        return (
            <>
                <ModalHeader>
                    <AddDocumentH2 />
                    <ModalClose onClick={closeModalPrompt} />
                </ModalHeader>
                {page === 1 ?
                    <React.Fragment>
                        <ModalSubHeader>
                            <div className="tab-header tab-header-text-select">Enter document details</div>
                        </ModalSubHeader>
                        <FirstPage viewType={view} documentId={props.documentId} nextAction={() => setPage(2)} closeAction={closeModalPrompt} />
                    </React.Fragment>
                    :
                    <SecondPage backAction={() => setPage(1)} completeAction={(document: Document) => addNewUpload(document)} isComplete={isComplete} />

                }
                <InfoModal text={successText} isOpen={isComplete} closeAction={() => { setIsComplete(false); closeModal(); }} />
                <InfoModal text={errorText} isOpen={isError} closeAction={() => setIsError(false)} />
                <ExitCancelModel
                    text={confirmCloseText}
                    actionButtonText={'Exit'}
                    closeButtonText={'Cancel'}
                    isOpen={isConfirmClose}
                    action={() => { setIsConfirmClose(false); closeModal(); }}
                    closeAction={() => setIsConfirmClose(false)} />
            </>
        );
    }

    const EditForm = () => {
        const [tab, setTab] = useState(view)
        const [page, setPage] = useState(1);
        const [isError, setIsError] = useState(false);
        const [isComplete, setIsComplete] = useState(false);
        const [isEditValid, setIsEditValid] = useState(false);
        const [showEditInvalid, setShowEditInvalid] = useState(false);
        const [isConfirmClose, setIsConfirmClose] = useState(false);

        const setEditView = (view: PageView) => {
            setTab(view);
        }

        const editUpload = async (document: Document) => {
            const editDoc = documentMapper.mapToNewDocument(document);
            if (!document.documentId) return;

            await editDocument(document.documentId, editDoc)
                .then(async (response) => {
                    if (response.ok) {
                        props.onUpdate();
                        setIsComplete(true);
                    }
                    else {
                        throw (await response.json());
                    }
                })
                .catch((e) => {
                    errorText = handleErrors(e) || errorText;
                    setIsError(true);
                });
        }

        const selectNewVersionTab = () => {
            if (isEditValid) {
                setEditView(PageView.NewVersion)
            } else {
                setShowEditInvalid(true);
            }
        }

        const closeModalPrompt = () => {
            setIsConfirmClose(true);
        }

        return (
            <>
                <ModalHeader>
                    <EditDocumentH2 />
                    <ModalClose onClick={closeModalPrompt} />
                </ModalHeader>
                {page === 1
                    ? <React.Fragment key="xx">
                        <ModalSubHeader>
                            <div style={{ display: "flex", alignContent: "flex-start" }} >
                                <div className="tab-header tab-header-spacer"> | </div>
                                <div style={{ cursor: 'pointer' }}
                                    className={tab === PageView.Edit ? 'tab-header tab-header-text-select selected' : 'tab-header tab-header-text'}
                                    onClick={() => setEditView(PageView.Edit)}>Update document details<div className="tab-header-triangle" /></div>
                                <div className="tab-header tab-header-spacer"> | </div>
                                <div style={{ cursor: 'pointer' }}
                                    className={tab === PageView.NewVersion ? 'tab-header tab-header-text-select selected' : 'tab-header tab-header-text'}
                                    onClick={selectNewVersionTab}>Add new version<div className="tab-header-triangle" /></div>
                                <div className="tab-header tab-header-spacer"> | </div>
                            </div>
                        </ModalSubHeader>

                        <FirstPage viewType={tab} documentId={props.documentId} nextAction={() => setPage(2)} closeAction={closeModalPrompt} validateAction={x => setIsEditValid(x)} />
                    </React.Fragment>
                    :
                    <SecondPage backAction={() => setPage(1)} completeAction={(document: Document) => editUpload(document)} isComplete={isComplete} />
                }
                <InfoModal text={successText} isOpen={isComplete} closeAction={() => { setIsComplete(false); closeModal(); }} />
                <InfoModal text={errorText} isOpen={isError} closeAction={() => setIsError(false)} />
                <InfoModal text="This document has invalid fields. Please edit the document before updating the version." isOpen={showEditInvalid} closeAction={() => setShowEditInvalid(false)} />
                <ExitCancelModel
                    text={confirmCloseText}
                    actionButtonText={'Exit'}
                    closeButtonText={'Cancel'}
                    isOpen={isConfirmClose}
                    action={() => { setIsConfirmClose(false); closeModal(); }}
                    closeAction={() => setIsConfirmClose(false)} />
            </>
        );
    }


    const RescindForm = () => {
        const [page, setPage] = useState(1);
        const [isError, setIsError] = useState(false);
        const [isComplete, setIsComplete] = useState(false);
        const [isConfirmClose, setIsConfirmClose] = useState(false);

        const rescind = async (document: Document) => {
            const recindDoc = documentMapper.mapToNewDocument(document);
            if (!props.documentId) return;

            await recindDocument(props.documentId, recindDoc)
                .then(async (response) => {
                    if (response.ok) {
                        props.onUpdate();
                        setIsComplete(true);
                    }
                    else {
                        throw (await response.json());
                    }
                })
                .catch((e) => {
                    errorText = handleErrors(e) || errorText;
                    setIsError(true);
                });
        }

        const closeModalPrompt = () => {
            setIsConfirmClose(true);
        }

        return (
            <>
                <ModalHeader>
                    <RescindDocumentH2 />
                    <ModalClose onClick={closeModalPrompt} />
                </ModalHeader>
                {page === 1
                    ? <React.Fragment>
                        <ModalSubHeader>
                            <div className="tab-header tab-header-text-select">Enter new document details</div>
                        </ModalSubHeader>
                        <FirstPage viewType={view} documentId={props.documentId} nextAction={() => setPage(2)} closeAction={closeModalPrompt} />
                    </React.Fragment>
                    : <React.Fragment>
                        <SecondPage backAction={() => setPage(1)} completeAction={(document: Document) => rescind(document)} isComplete={isComplete} />
                    </React.Fragment>
                }
                <InfoModal text={successText} isOpen={isComplete} closeAction={() => { setIsComplete(false); closeModal(); }} />
                <InfoModal text={errorText} isOpen={isError} closeAction={() => setIsError(false)} />
                <ExitCancelModel
                    text={confirmCloseText}
                    actionButtonText={'Exit'}
                    closeButtonText={'Cancel'}
                    isOpen={isConfirmClose}
                    action={() => { setIsConfirmClose(false); closeModal(); }}
                    closeAction={() => setIsConfirmClose(false)} />
            </>
        );
    }

    const renderSwitch = () => {

        switch (view) {
            case PageView.AddNew:
                successText = "Congratulations! Your document has been uploaded";
                return (useMemo(() => (<AddNewForm key="addForm" />), []));
            case PageView.NewVersion:
            case PageView.Edit:
                return (useMemo(() => (<EditForm key="editForm" />), []));
            case PageView.Rescind:
                successText = "Document rescinded successfully";
                return (useMemo(() => (<RescindForm key="rescindForm" />), []));
            default:
                return (<div />);
        }
    }
    return (
        <>
            {renderSwitch()}
        </>
    );
};

export const ModalHost = (props: IDocumentFormProps) => {
    return (
        <div>
            <Modal key="modalhost1"
                isOpen={props.isOpen}
                className="document-admin-modal">
                <DocumentForm key="editor1" {...props} />
            </Modal>
        </div>
    );
}

export const AddNewForm = (props: IAddNewFormProps) => (<ModalHost {...props} viewType={PageView.AddNew} />);
export const EditForm = (props: IEditFormProps) => (<ModalHost {...props} viewType={PageView.Edit} />);
export const NewVersionForm = (props: IEditFormProps) => (<ModalHost {...props} viewType={PageView.NewVersion} />);
export const RescindForm = (props: IEditFormProps) => (<ModalHost {...props} viewType={PageView.Rescind} />);
